import React, { useEffect, useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import JuryBoxAppBar from '../../components/JuryBoxAppBar';
import MaterialTableModule from 'material-table';
const MaterialTable = MaterialTableModule.default || MaterialTableModule;
import { tableIcons } from '../../util/icons';
import { useAlert } from '../../util/alertDialogContext';
import { useNavigate } from 'react-router-dom';
import CreateModal from './CreateModal';
import Button from '@mui/material/Button';
import Add from '@mui/icons-material/Add';
import Description from '@mui/icons-material/Description';
import LinearProgress from '@mui/material/LinearProgress';
import { useDispatch, useSelector } from 'react-redux';
import { triggerSync, fetchAllCases, cloneCase } from '../../db/actions';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import { useCases, useDatabase } from '../../db/hooks';
import { deleteCase, archiveCase, restoreCase } from '../../db/actions';
import { ActionCreators } from '../../state/actions';
import OfflineBar from '../../components/OfflineBar';
import moment from 'moment';
import ShareModal from './ShareModal';
import { useAuth } from '../../util/authContext';

const columns = [
  {
    title: 'Number',
    field: 'number',
  },
  { title: 'Name', field: 'name' },
  { title: 'Comments', field: 'details' },
  { title: 'Creator', field: 'creator' },
  {
    title: 'Created', field: 'createdAt', render: (rowData) => {
      return moment(rowData.createdAt).format('YYYY-MM-DD h:mm a');
    },
  },
  {
    title: 'Status',
    field: 'archived',
    sorting: false,
    render: (rowData) => (
      <Stack direction="row" spacing={1}>
        {rowData.archived && (
          <Chip
            label="Archived"
            size="small"
            title={rowData.archivedAt ? `Archived ${moment(rowData.archivedAt).format('YYYY-MM-DD h:mm a')}` : undefined}
          />
        )}
        {!rowData.isOwner && <Chip label="Shared" size="small" color="info" variant="outlined" />}
        {!rowData.isOwner && !rowData.canEdit && (
          <Chip label="Read-only" size="small" variant="outlined" />
        )}
      </Stack>
    ),
  },
];

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    padding: theme.spacing(2),
  },
  paper: {
    width: '100%',
  },
  table: {
    '& tbody>.MuiTableRow-root:hover': {
      background: 'rgba(0, 0, 0, 0.07)',
    },
  },
}));

const CaseList = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Use Dexie hook for cases
  const { cases: dexieCases, isLoading: dexieLoading } = useCases();
  const { isReady: dbReady } = useDatabase();
  const { isAppReady, data: authUser } = useAuth();

  // Also get Redux state for loading/error states during transition
  const caseListLoading = useSelector(state => state.app.caseListLoading) || dexieLoading;
  const caseListError = useSelector(state => state.app.caseListError);
  const casesErrors = useSelector(state => state.app.casesErrors) || {};
  // Redux sessionData is only populated by the login flow — after a page
  // reload it's empty, which made every case look like someone else's.
  // The auth context restores the user from localStorage on reload.
  const reduxSessionEmail = useSelector(state => state.app.sessionData?.email);
  const sessionEmail = authUser?.email || reduxSessionEmail;

  console.log("Dexie cases: ", dexieCases);
  
  // Use Dexie cases, format to match expected structure
  const caseList = dexieCases.map(c => {
    const isOwner = !c.creator || c.creator === sessionEmail;
    // Server sets canShare on fetch; older/migrated/local cases may omit it.
    // For shared cases canShare doubles as the edit-access signal.
    const canShare = c.canShare !== undefined ? c.canShare : isOwner;

    return {
      _id: c._id,
      name: c.name,
      number: c.number,
      details: c.details,
      creator: c.creator,
      createdAt: c.createdAt,
      updatedAt: c.updatedAt,
      // Cases synced before the archive feature have no archived field;
      // treat them as active.
      archived: c.archived === true,
      archivedAt: c.archivedAt,
      isOwner,
      canDelete: isOwner,
      canEdit: isOwner || canShare === true,
      canShare,
    };
  });

  console.log("Case list: ", caseList);

  let caseListWithErrors = caseList.map((caseListItem) => {
    let error = undefined;
    if (casesErrors[caseListItem._id]) {
      //console.log("Error: ", casesErrors[caseListItem._id].error);
      error = casesErrors[caseListItem._id].error.message;
    }
    return {
      ...caseListItem,
      error,
    };
  }).sort((caseA, caseB) => {
    if (caseA.createdAt && caseB.createdAt) {
      return (caseA.createdAt.localeCompare(caseB.createdAt) * -1); // Largest to smallest
    }
    return 0;
  });

  console.log("Case list with errors: ", caseListWithErrors);

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [shareModel, setShareModel] = useState({
    open: false,
    caseId: null,
    caseName: null
  });

  // Archived cases stay in the local DB so they remain searchable offline,
  // but are hidden from the list unless the user opts in.
  const archivedCount = caseListWithErrors.filter((c) => c.archived).length;
  const visibleCases = includeArchived
    ? caseListWithErrors
    : caseListWithErrors.filter((c) => !c.archived);

  const { alert } = useAlert();

  const handleCreateCase = async (caseId) => {
    setCreateModalOpen(false);
    //console.log('Navigating to case id: ', caseId);
    navigate(`/case/${caseId}`);
  };

  const handleListCases = async () => {
    // Fetch cases from server and push any pending local changes
    try {
      await fetchAllCases();
      await triggerSync();
    } catch (err) {
      console.warn('Failed to list cases:', err);
    }
  };

  useEffect(() => {
    // Fetch cases from server on mount and when create modal closes
    if (!createModalOpen && dbReady && isAppReady) {
      fetchAllCases().catch(err => console.warn('Failed to fetch cases:', err));
      triggerSync();
    }
  }, [createModalOpen, dbReady, isAppReady]);

  return (
    <div id="app-wrapper">
      <OfflineBar/>
      <JuryBoxAppBar title={'Cases'}/>
      {caseListLoading && <LinearProgress/>}
      {!!caseListError && (
        <Alert severity={'error'}
               onClose={() => {
                 dispatch(ActionCreators.removeCaseListError());
               }}
               key={'case-list-error'}>{caseListError.error.toString()}</Alert>
      )}
      <div className={classes.root}>
        <Stack direction="row" justifyContent="flex-end" alignItems="center">
          <FormControlLabel
            control={
              <Switch
                checked={includeArchived}
                onChange={(event) => setIncludeArchived(event.target.checked)}
                size="small"
              />
            }
            label={`Include archived cases${archivedCount > 0 ? ` (${archivedCount})` : ''}`}
          />
        </Stack>
        <div className={classes.table}>
          <MaterialTable
            columns={columns}
            data={visibleCases}
            options={{
              showTitle: false,
              searchFieldAlignment: 'left',
              search: true,
              paging: false,
              sorting: true,
              headerStyle: {
                fontWeight: 'bold',
                fontSize: 16,
              },
              actionsColumnIndex: -1,
            }}
            icons={tableIcons}
            actions={[
              {
                icon: tableIcons.Add,
                tooltip: 'Create Case',
                isFreeAction: true,
                onClick: () => setCreateModalOpen(true),
              },
              {
                icon: () => <Description />,
                tooltip: 'Manage templates',
                isFreeAction: true,
                onClick: () => navigate('/templates'),
              },
              {
                icon: tableIcons.Refresh,
                tooltip: 'Refresh list',
                isFreeAction: true,
                onClick: () => handleListCases(),
              },
              (rowData) => ({
                icon: tableIcons.Clone,
                tooltip: "Clone Case",
                onClick: (event) => {
                  event.preventDefault();
                  alert({
                    msg:
                      'Are you sure you want to make a clone of this case?',
                    onSubmit: () => cloneCase(rowData._id),
                    variant: 'danger',
                  });
                }
              }),
              (rowData) => ({
                icon: tableIcons.Share,
                tooltip: rowData.canShare ? 'Share Case' : "Can't share cases created by others",
                disabled: !rowData.canShare,
                onClick: (event) => {
                  event.preventDefault();
                  setShareModel({
                    open: true,
                    caseId: rowData._id,
                    caseName: rowData.name,
                    loading: false
                  });
                }
              }),
              (rowData) => ({
                icon: tableIcons.Archive,
                hidden: rowData.archived,
                tooltip: rowData.canEdit ? 'Archive Case' : "Can't archive read-only cases",
                disabled: !rowData.canEdit,
                onClick: (event) => {
                  event.preventDefault();
                  alert({
                    msg:
                      'Archive this case? Archived cases are hidden from your case list, but can be found again with "Include archived cases".',
                    onSubmit: async () => await archiveCase(rowData._id, sessionEmail),
                    variant: 'danger',
                  });
                },
              }),
              (rowData) => ({
                icon: tableIcons.Restore,
                hidden: !rowData.archived,
                tooltip: rowData.canEdit ? 'Restore Case' : "Can't restore read-only cases",
                disabled: !rowData.canEdit,
                onClick: async (event) => {
                  event.preventDefault();
                  await restoreCase(rowData._id);
                },
              }),
              (rowData) => ({
                icon: tableIcons.Delete,
                tooltip: rowData.canDelete ? 'Delete Case' : "Can't delete cases created others",
                disabled: !rowData.canDelete,
                onClick: (event) => {
                  event.preventDefault();
                  alert({
                    msg:
                      'Deleting this case is permanent — unlike archiving, it cannot be undone. Would you like to proceed?',
                    onSubmit: async () => await deleteCase(rowData._id),
                    variant: 'danger',
                  });
                },
              }),
              (rowData) => ({
                icon: tableIcons.Error,
                hidden: rowData.error === undefined,
                tooltip: rowData.error ? rowData.error : '',
                onClick: (event) => {
                  event.preventDefault();
                  // Nothing to do really
                  dispatch(ActionCreators.removeCaseError(rowData._id))
                },
              }),
            ]}
            onRowClick={(event, rowData) => {
              //console.log('Navigating to case id: ', rowData._id);
              // Case identity rides along so the loading screen can show it
              // before the case data is decrypted locally.
              navigate(`/case/${rowData._id}`, {
                state: { caseName: rowData.name, caseNumber: rowData.number },
              });
            }}
            localization={{
              body: {
                emptyDataSourceMessage: (
                  <div style={{ padding: '25px' }}>
                    {!includeArchived && archivedCount > 0 ? (
                      <Typography color="textSecondary" gutterBottom>
                        No active cases found. Turn on “Include archived cases” to see your
                        {` ${archivedCount} `}archived {archivedCount === 1 ? 'case' : 'cases'}.
                      </Typography>
                    ) : (
                      <Typography color="textSecondary" gutterBottom>
                        No cases found.
                      </Typography>
                    )}
                    <Button
                      onClick={() => setCreateModalOpen(true)}
                      variant="contained"
                      color="secondary"
                      startIcon={<Add/>}
                    >
                      Add Case
                    </Button>
                  </div>
                ),
              },
            }}
          />
        </div>
      </div>
      <CreateModal
        open={createModalOpen}
        key={'create-modal'}
        onClose={() => setCreateModalOpen(false)}
        onCreate={handleCreateCase}
      />
      <ShareModal open={shareModel.open}
                  caseId={shareModel.caseId}
                  caseName={shareModel.caseName}
                  key={'share-modal'} onClose={() => setShareModel({
        open: false,
        caseId: null,
        caseName: null
      })}/>
    </div>
  );
};

export default CaseList;
